import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import MenuBar from "../components/common/MenuBar";
import {
  Container,
  TableWrapper,
  StyledTable,
  TableRow,
  TableHeader,
  TableCell,
  StatusBadge,
  ActionButton,
  FilterContainer,
  FilterInput,
  FilterSelect,
  LoadMoreButton,
} from "./styles/SubjectList.styles";

const API_ENDPOINT =
  "https://bap7u9i7ig.execute-api.ap-northeast-2.amazonaws.com/prod";

const FRONTEND_GROUP_ID = "as7dk9f3n5v2x";

// 실제 DB에서 데이터를 가져오는 함수
const fetchSubjects = async () => {
  try {
    const response = await fetch(`${API_ENDPOINT}/subjects`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error("데이터를 가져오는데 실패했습니다.");
    }

    const result = await response.json();
    let parsedResponse;

    try {
      parsedResponse = typeof result === "string" ? JSON.parse(result) : result;
      if (typeof parsedResponse.body === "string") {
        parsedResponse = JSON.parse(parsedResponse.body);
      }
    } catch (error) {
      console.error("응답 파싱 오류:", error);
      parsedResponse = result;
    }

    if (parsedResponse.success) {
      return parsedResponse.data;
    } else {
      throw new Error(
        parsedResponse.error || "데이터를 가져오는데 실패했습니다."
      );
    }
  } catch (error) {
    console.error("데이터 가져오기 오류:", error);
    throw error;
  }
};

const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString("ko-KR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
};

const SubjectList = () => {
  const navigate = useNavigate();
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [visibleCount, setVisibleCount] = useState(20);
  const [filters, setFilters] = useState({
    search: "",
    status: "all",
    sex: "all",
  });
  const [sort, setSort] = useState({
    field: null,
    direction: null,
  });

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    const loadSubjects = async () => {
      try {
        setLoading(true);
        const data = await fetchSubjects();
        setSubjects(data);
        setError(null);
      } catch (err) {
        setError(err.message);
        console.error("데이터 로드 실패:", err);
      } finally {
        setLoading(false);
      }
    };

    loadSubjects();
  }, []);

  const handleSort = (field) => {
    setSort((prev) => ({
      field,
      direction:
        prev.field === field
          ? prev.direction === "asc"
            ? "desc"
            : prev.direction === "desc"
            ? null
            : "asc"
          : "asc",
    }));
  };

  const filteredAndSortedSubjects = useMemo(() => {
    let result = subjects.filter((subject) => {
      const matchesSearch =
        subject.subjectId
          .toLowerCase()
          .includes(filters.search.toLowerCase()) ||
        subject.name.toLowerCase().includes(filters.search.toLowerCase());
      const matchesStatus =
        filters.status === "all" || subject.status === filters.status;
      const matchesSex = filters.sex === "all" || subject.sex === filters.sex;

      return matchesSearch && matchesStatus && matchesSex;
    });

    if (sort.field && sort.direction) {
      result = [...result].sort((a, b) => {
        let compareA = a[sort.field];
        let compareB = b[sort.field];

        if (sort.field === "subjectId") {
          const numA = parseInt(compareA.split("-")[1]);
          const numB = parseInt(compareB.split("-")[1]);
          return sort.direction === "asc" ? numA - numB : numB - numA;
        }

        if (sort.field === "createdDate" || sort.field === "lastModified") {
          compareA = new Date(compareA);
          compareB = new Date(compareB);
        }

        if (sort.field === "age") {
          return sort.direction === "asc"
            ? Number(compareA) - Number(compareB)
            : Number(compareB) - Number(compareA);
        }

        if (compareA < compareB) return sort.direction === "asc" ? -1 : 1;
        if (compareA > compareB) return sort.direction === "asc" ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [subjects, filters, sort]);

  const handleAddNew = () => {
    navigate("../newcase");
  };

  const handleLoadMore = () => {
    setVisibleCount((prev) =>
      Math.min(prev + 20, filteredAndSortedSubjects.length)
    );
  };

  if (loading) {
    return (
      <Container style={{ marginTop: "70px", textAlign: "center" }}>
        <p>데이터를 불러오는 중...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container style={{ marginTop: "70px", textAlign: "center" }}>
        <p>오류가 발생했습니다: {error}</p>
      </Container>
    );
  }

  return (
    <>
      <MenuBar />
      <Container style={{ marginTop: "70px" }}>
        <FilterContainer>
          <FilterInput
            type="text"
            placeholder="ID 또는 이름으로 검색..."
            value={filters.search}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, search: e.target.value }))
            }
          />
          <FilterSelect
            value={filters.status}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, status: e.target.value }))
            }
          >
            <option value="all">모든 상태</option>
            <option value="Completed">완료</option>
            <option value="In Progress">진행 중</option>
            <option value="Not Started">시작 전</option>
          </FilterSelect>
          <FilterSelect
            value={filters.sex}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, sex: e.target.value }))
            }
          >
            <option value="all">모든 성별</option>
            <option value="Male">남성</option>
            <option value="Female">여성</option>
          </FilterSelect>
        </FilterContainer>

        <TableWrapper>
          <StyledTable>
            <thead>
              <TableRow>
                <TableHeader
                  sortable
                  onClick={() => handleSort("subjectId")}
                  sortDirection={
                    sort.field === "subjectId" ? sort.direction : null
                  }
                >
                  Subject ID
                </TableHeader>
                <TableHeader
                  sortable
                  onClick={() => handleSort("name")}
                  sortDirection={sort.field === "name" ? sort.direction : null}
                >
                  이름
                </TableHeader>
                <TableHeader
                  sortable
                  onClick={() => handleSort("age")}
                  sortDirection={sort.field === "age" ? sort.direction : null}
                >
                  나이
                </TableHeader>
                <TableHeader
                  sortable
                  onClick={() => handleSort("sex")}
                  sortDirection={sort.field === "sex" ? sort.direction : null}
                >
                  성별
                </TableHeader>
                <TableHeader
                  sortable
                  onClick={() => handleSort("status")}
                  sortDirection={
                    sort.field === "status" ? sort.direction : null
                  }
                >
                  상태
                </TableHeader>
                <TableHeader
                  sortable
                  onClick={() => handleSort("createdDate")}
                  sortDirection={
                    sort.field === "createdDate" ? sort.direction : null
                  }
                >
                  생성일
                </TableHeader>
                <TableHeader
                  sortable
                  onClick={() => handleSort("lastModified")}
                  sortDirection={
                    sort.field === "lastModified" ? sort.direction : null
                  }
                >
                  마지막 수정일
                </TableHeader>
                <TableHeader>작업</TableHeader>
              </TableRow>
            </thead>
            <tbody>
              {filteredAndSortedSubjects
                .slice(0, visibleCount)
                .map((subject) => (
                  <TableRow key={subject.id}>
                    <TableCell>{subject.subjectId}</TableCell>
                    <TableCell>{subject.name}</TableCell>
                    <TableCell>{subject.age}</TableCell>
                    <TableCell>
                      {subject.sex === "Male"
                        ? "남성"
                        : subject.sex === "Female"
                        ? "여성"
                        : subject.sex}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={subject.status}>
                        {subject.status === "Completed"
                          ? "완료"
                          : subject.status === "In Progress"
                          ? "진행 중"
                          : subject.status === "Not Started"
                          ? "시작 전"
                          : subject.status}
                      </StatusBadge>
                    </TableCell>
                    <TableCell>{formatDate(subject.createdDate)}</TableCell>
                    <TableCell>{formatDate(subject.lastModified)}</TableCell>
                    <TableCell>
                      <ActionButton
                        edit
                        onClick={() =>
                          navigate(`/${FRONTEND_GROUP_ID}/edit/${subject.id}`)
                        }
                      >
                        수정
                      </ActionButton>
                      <ActionButton
                        delete
                        onClick={() => console.log("Delete", subject.id)}
                      >
                        삭제
                      </ActionButton>
                    </TableCell>
                  </TableRow>
                ))}
            </tbody>
          </StyledTable>
          {visibleCount < filteredAndSortedSubjects.length && (
            <LoadMoreButton onClick={handleLoadMore}>
              더 보기 ({filteredAndSortedSubjects.length - visibleCount}개 남음)
            </LoadMoreButton>
          )}
        </TableWrapper>
      </Container>
    </>
  );
};

export default SubjectList;
